import * as React from 'react';
import {
  Box,
  DialogContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
} from '@mui/material';

import { useSelector, useDispatch } from 'react-redux'
import { createSlide, updateSlide, previewSlide } from '../slices/presentation';
import { CDialog, CDialogTitle, CDialogActions, CButton, CTextField } from '../components/common/Modal';

export default function SlideModal(props) {
  const dbs = useSelector(state => state.auth.user.db);

  const {open, defaultTitle, defaultPrompt, defaultVisualization, defaultDatabase, defaultComment, handleClose, pId, index, updateMode} = props;
  const [loading, setLoading] = React.useState(false);

  const [title, setTitle] = React.useState(defaultTitle);
  const [prompt, setPrompt] = React.useState(defaultPrompt);
  const [visualization, setVisualization] = React.useState(defaultVisualization);
  const [database, setDatabase] = React.useState(defaultDatabase);
  const [comment, setComment] = React.useState(defaultComment);

  // Column selection state (step 2)
  const [step, setStep] = React.useState(1);
  const [columns, setColumns] = React.useState([]);
  const [xColumn, setXColumn] = React.useState([]);
  const [yColumn, setYColumn] = React.useState('');

  const dispatch = useDispatch();

  React.useEffect(() => {
    setTitle(defaultTitle);
    setPrompt(defaultPrompt);
    setVisualization(defaultVisualization);
    setDatabase(defaultDatabase);
    setComment(defaultComment);
  }, [defaultTitle, defaultPrompt, defaultVisualization, defaultDatabase, defaultComment]);

  // Reset step when modal opens/closes
  React.useEffect(() => {
    if (!open) {
      setStep(1);
      setColumns([]);
      setXColumn('');
      setYColumn('');
    }
  }, [open]);

  const needsColumnSelection = visualization === 'chart' || visualization === 'ultra';

  const handleNext = () => {
    if (prompt === '') {
      window.alert('Please input the prompt.');
      return;
    }
    if (!needsColumnSelection) {
      handleSubmit();
      return;
    }
    setLoading(true);
    dispatch(previewSlide({ source: database, prompt }))
      .then((action) => {
        setLoading(false);
        if (action.payload && action.payload.columns) {
          setColumns(action.payload.columns);
          setXColumn(action.payload.columns.slice(0, 1));
          setYColumn(action.payload.columns[1] || '');
          setStep(2);
        }
      })
      .catch(() => setLoading(false));
  };

  const handleSubmit = () => {
    setLoading(true);
    const info = {
      presentation: pId,
      title,
      source: database,
      prompt,
      comment,
      visualization,
      x_column: needsColumnSelection ? xColumn : undefined,
      y_column: needsColumnSelection ? yColumn : undefined,
    };
    const action = updateMode
      ? updateSlide({ ...info, index })
      : createSlide(info);

    dispatch(action)
      .then(() => {
        setLoading(false);
        handleClose();
      })
      .catch(() => setLoading(false));
  };

  return (
    <>
      <CDialog open={open} onClose={handleClose} maxWidth={'md'} fullWidth>
        <CDialogTitle>{step === 1 ? 'New Slide' : 'Select Chart Axes'}</CDialogTitle>
        <DialogContent>
          {step === 1 && (
            <>
              <CTextField
                id="title"
                label="Title"
                size="small"
                rows={3}
                variant="outlined"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                sx={{ marginTop: '1rem' }}
                fullWidth
              />
              <FormControl sx={{ marginTop: '1rem' }} size="small" fullWidth>
                <InputLabel id="db-label">Data Source</InputLabel>
                <Select
                  labelId="db-label"
                  value={database}
                  margin="dense"
                  label="Data Source"
                  onChange={(e) => setDatabase(e.target.value)}
                  fullWidth
                  variant="outlined"
                >
                  {dbs && dbs.length > 0 && dbs.map(item => (
                    <MenuItem key={item.name} value={item.name}>{item.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <CTextField
                id="prompt"
                label="Prompt"
                size="small"
                multiline
                rows={3}
                variant="outlined"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                sx={{ marginTop: '1rem' }}
                fullWidth
              />
              <Grid spacing={2} container>
                <Grid xs={6} item>
                  <FormControl sx={{ marginTop: '1rem' }} size="small" fullWidth>
                    <InputLabel id="vis-label">Visualization</InputLabel>
                    <Select
                      labelId="vis-label"
                      value={visualization}
                      margin="dense"
                      label="Visualization"
                      onChange={(e) => setVisualization(e.target.value)}
                      fullWidth
                      variant="outlined"
                    >
                      <MenuItem value="chart">Visualization</MenuItem>
                      <MenuItem value="table">Data Table</MenuItem>
                      <MenuItem value="ultra">Visualization & Table</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid xs={6} item>
                  <FormControlLabel
                    control={<Checkbox checked={comment} onChange={(e) => setComment(e.target.checked)} />}
                    label="Comment"
                  />
                </Grid>
              </Grid>
            </>
          )}

          {step === 2 && (
            <>
              <Typography variant="body2" sx={{ marginTop: '1rem', marginBottom: '0.5rem', color: 'text.secondary' }}>
                Choose which columns to use for the chart axes.
              </Typography>
              <FormControl sx={{ marginTop: '1rem' }} size="small" fullWidth>
                <InputLabel id="x-col-label">X Axis (Categories)</InputLabel>
                <Select
                  labelId="x-col-label"
                  multiple
                  value={xColumn}
                  label="X Axis (Categories)"
                  onChange={(e) => setXColumn(e.target.value)}
                  renderValue={(selected) => selected.join(', ')}
                  fullWidth
                  variant="outlined"
                >
                  {columns.map(col => (
                    <MenuItem key={col} value={col}>{col}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ marginTop: '1rem' }} size="small" fullWidth>
                <InputLabel id="y-col-label">Y Axis (Values)</InputLabel>
                <Select
                  labelId="y-col-label"
                  value={yColumn}
                  label="Y Axis (Values)"
                  onChange={(e) => setYColumn(e.target.value)}
                  fullWidth
                  variant="outlined"
                >
                  {columns.map(col => (
                    <MenuItem key={col} value={col}>{col}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {loading && (
            <Box className="absolute w-full h-full" sx={{ top: 0, left: 0 }}>
              <Box className="relative w-full h-full">
                <Box className="w-full h-full flex justify-center align-center">
                  <CircularProgress sx={{ opacity: 0.7 }} />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <CDialogActions>
          {step === 2 && (
            <CButton onClick={() => setStep(1)} disabled={loading}>Back</CButton>
          )}
          <CButton onClick={handleClose}>Cancel</CButton>
          {step === 1 && (
            <CButton onClick={handleNext} disabled={loading}>
              {needsColumnSelection ? 'Next' : 'Ok'}
            </CButton>
          )}
          {step === 2 && (
            <CButton onClick={handleSubmit} disabled={loading || !xColumn.length || !yColumn}>Ok</CButton>
          )}
        </CDialogActions>
      </CDialog>
    </>
  );
}
